import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
} from "@mui/material";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { Simulation, SimulationStatus } from "../../../interfaces/DTO/simulation";
import { formatSimulationStatus } from "../../../utils/simulationUtil";

interface SaveSimulationDialogProps {
  open: boolean;
  onClose: () => void;
  existingSimulation?: Simulation | null;
  metadataOnly?: boolean;
  modifiedRows?: MimshakData[];
  originalRows?: MimshakData[];
  onSaveNew: (name: string, notes: string, status: SimulationStatus) => Promise<void>;
  onUpdateSimulation?: (name: string, notes: string, status: SimulationStatus) => Promise<void>;
}

const SaveSimulationDialog: React.FC<SaveSimulationDialogProps> = ({
  open,
  onClose,
  existingSimulation,
  metadataOnly,
  modifiedRows,
  originalRows,
  onSaveNew,
  onUpdateSimulation,
}) => {
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<SimulationStatus>(SimulationStatus.DRAFT);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(metadataOnly ? (existingSimulation?.name ?? "") : "");
      setNotes(metadataOnly ? (existingSimulation?.notes ?? "") : "");
      setStatus(existingSimulation?.status ?? SimulationStatus.DRAFT);
      setError(null);
    }
  }, [open, existingSimulation, metadataOnly]);

  const validateNewProducts = (): boolean => {
    if (!modifiedRows || metadataOnly) return true;
    const originalPolicyNumbers = new Set((originalRows ?? []).map((r) => r.policy_number));
    const newProductsWithNullType = modifiedRows.filter(
      (r) => !originalPolicyNumbers.has(r.policy_number) && (r.adjusted_product_type === null || r.adjusted_product_type === undefined)
    );
    if (newProductsWithNullType.length > 0) {
      setError("לא ניתן לשמור — יש מוצר חדש ללא סוג מוצר. יש לבחור סוג מוצר לפני השמירה.");
      return false;
    }
    return true;
  };

  const handleAction = async () => {
    if (!name.trim()) return;
    if (!validateNewProducts()) return;
    setLoading(true);
    try {
      if (metadataOnly && onUpdateSimulation) {
        await onUpdateSimulation(name.trim(), notes, status);
      } else {
        await onSaveNew(name.trim(), notes, status);
      }
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle>{metadataOnly ? "עריכת פרטי סימולציה" : "שמור סימולציה חדשה"}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
          <TextField
            label="שם סימולציה"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="הערות"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
          <FormControl fullWidth>
            <InputLabel>סטטוס</InputLabel>
            <Select
              value={status}
              label="סטטוס"
              onChange={(e) => setStatus(e.target.value as SimulationStatus)}
              // סימולציה שאושרה לביצוע כבר יצרה משימות – הסטטוס לא חוזר אחורה ידנית
              disabled={existingSimulation?.status === SimulationStatus.APPROVED || existingSimulation?.status === SimulationStatus.DISCARDED}
            >
              {Object.values(SimulationStatus)
                // "אושר לביצוע" מתקבל רק דרך כפתור האישור (יוצר משימות), לא בבחירה ידנית
                .filter((s) => s !== SimulationStatus.APPROVED || status === SimulationStatus.APPROVED)
                // "מבוטל" רק דרך הסרת הסימולציה (מבטלת את המשימות ושומרת את הסיבה)
                .filter((s) => s !== SimulationStatus.DISCARDED || status === SimulationStatus.DISCARDED)
                .map((s) => (
                  <MenuItem key={s} value={s}>
                    {formatSimulationStatus(s)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          ביטול
        </Button>
        <Button
          onClick={handleAction}
          variant="contained"
          color="primary"
          disabled={!name.trim() || loading}
        >
          {metadataOnly ? "שמור" : "שמור סימולציה"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveSimulationDialog;
